import { decodeBrr, encodeBrr } from "./brr";
import { IT_C5, NOTE_CUT, TrackerSong, effectById, finalizeSound, playableNote } from "./tracker";

// plays a song into audio, the same way the exported file would play it: rows, speed and tempo, note cuts, loops, and
// the few effects the tracker allows. the player in the app, the .wav exports and the .mid export all share this.
// a row lasts speed * 2.5 / tempo seconds, which is how impulse tracker times it

export interface Step {
  time: number;
  order: number;
  pattern: number;
  row: number;
  speed: number;
  tempo: number;
}

export interface Timeline {
  steps: Step[];
  length: number;
}

const rowSeconds = (speed: number, tempo: number): number => (speed * 2.5) / tempo;

// every row the song visits in order, with when it starts. a jump back to a row already played ends the song
export function timeline(song: TrackerSong, maxSeconds = 900): Timeline {
  const steps: Step[] = [];
  const seen = new Set<string>();
  let position = 0;
  let row = 0;
  let speed = song.speed;
  let tempo = song.tempo;
  let time = 0;
  while (position < song.order.length && time < maxSeconds) {
    const pattern = song.patterns[song.order[position]];
    if (!pattern || row >= pattern.rows) {
      position++;
      row = 0;
      continue;
    }
    const key = `${position}:${row}`;
    if (seen.has(key)) break;
    seen.add(key);
    let jump = -1;
    let breakTo = -1;
    for (const note of pattern.notes) {
      if (note.r !== row || !note.fx) continue;
      const value = note.fp ?? effectById(note.fx)?.start ?? 0;
      if (note.fx === "A") speed = Math.max(1, value);
      else if (note.fx === "T") tempo = Math.max(32, value);
      else if (note.fx === "B") jump = value;
      else if (note.fx === "C") breakTo = value;
    }
    steps.push({ time, order: position, pattern: song.order[position], row, speed, tempo });
    time += rowSeconds(speed, tempo);
    if (jump >= 0) {
      position = jump;
      row = Math.max(0, breakTo);
    } else if (breakTo >= 0) {
      position++;
      row = breakTo;
    } else row++;
  }
  return { steps, length: time };
}

// --- mixing

interface Prepared {
  data: Float32Array;
  rate: number;
  loop: boolean;
  loopStart: number;
  loopEnd: number;
  volume: number;
}

export function prepareSounds(song: TrackerSong, snes: boolean): Prepared[] {
  return song.sounds.map((sound) => {
    const final = finalizeSound(sound);
    let data: Float32Array;
    if (snes && final.data.length) {
      // what the chip hears: the sample squeezed into brr and back
      const decoded = decodeBrr(encodeBrr(final.data, final.loop ? final.loopStart / 16 : -1));
      data = Float32Array.from(decoded, (value) => value / 32768);
    } else {
      data = Float32Array.from(final.data, (value) => value / 128);
    }
    return { data, rate: final.rate, loop: final.loop, loopStart: final.loopStart, loopEnd: final.loopEnd, volume: final.volume / 64 };
  });
}

interface Voice {
  sound: Prepared | null;
  position: number;
  step: number;
  gain: number;
}

export interface Rendered {
  samples: Float32Array;
  voices: Float32Array[];
  rate: number;
  // when each row starts, in seconds, for a moving cursor
  steps: Step[];
  length: number;
}

export interface RenderOptions {
  rate?: number;
  // run the sounds through brr first
  snes?: boolean;
  // also give each voice as its own track
  stems?: boolean;
  // seconds added after the last row so the final note can ring out
  tail?: number;
}

const MASTER = 0.5;

export function renderSong(song: TrackerSong, options: RenderOptions = {}): Rendered {
  const rate = options.rate || 44100;
  const line = timeline(song);
  const sounds = prepareSounds(song, Boolean(options.snes));
  const total = Math.ceil((line.length + (options.tail || 0)) * rate);
  const samples = new Float32Array(total);
  const voiceCount = Math.max(1, ...song.patterns.flatMap((pattern) => pattern.notes.map((note) => note.c + 1)));
  const stems = options.stems ? Array.from({ length: voiceCount }, () => new Float32Array(total)) : [];
  const voices: Voice[] = Array.from({ length: voiceCount }, () => ({ sound: null, position: 0, step: 0, gain: 0 }));

  line.steps.forEach((step, index) => {
    const from = Math.round(step.time * rate);
    const to = index + 1 < line.steps.length ? Math.round(line.steps[index + 1].time * rate) : Math.round(line.length * rate);
    for (const raw of song.patterns[step.pattern].notes) {
      if (raw.r !== step.row) continue;
      const note = playableNote(song, raw);
      const voice = voices[note.c];
      if (note.n === NOTE_CUT) {
        voice.sound = null;
        continue;
      }
      const sound = sounds[note.s];
      if (!sound || !sound.data.length) continue;
      voice.sound = sound;
      voice.position = 0;
      voice.step = (sound.rate * 2 ** ((note.n - IT_C5) / 12)) / rate;
      voice.gain = (note.v >= 0 ? Math.min(64, note.v) / 64 : 1) * sound.volume * MASTER;
    }
    for (let v = 0; v < voices.length; v++) {
      const voice = voices[v];
      const sound = voice.sound;
      if (!sound) continue;
      const out = stems[v];
      for (let i = from; i < to && i < total; i++) {
        const base = Math.floor(voice.position);
        const next = base + 1 < sound.data.length ? base + 1 : sound.loop ? sound.loopStart : base;
        const mix = voice.position - base;
        const value = (sound.data[base] * (1 - mix) + sound.data[next] * mix) * voice.gain;
        samples[i] += value;
        if (out) out[i] += value;
        voice.position += voice.step;
        if (sound.loop && voice.position >= sound.loopEnd) voice.position = sound.loopStart + ((voice.position - sound.loopStart) % (sound.loopEnd - sound.loopStart));
        else if (voice.position >= sound.data.length) {
          voice.sound = null;
          break;
        }
      }
    }
  });
  for (let i = 0; i < total; i++) samples[i] = Math.max(-1, Math.min(1, samples[i]));
  return { samples, voices: stems, rate, steps: line.steps, length: line.length };
}

// --- files

export function wavFile(samples: Float32Array, rate: number): Uint8Array {
  const out = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(out.buffer);
  const text = (at: number, value: string): void => value.split("").forEach((char, i) => view.setUint8(at + i, char.charCodeAt(0)));
  text(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  text(8, "WAVEfmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, samples.length * 2, true);
  samples.forEach((value, i) => view.setInt16(44 + i * 2, Math.round(Math.max(-1, Math.min(1, value)) * 32767), true));
  return out;
}

function variable(value: number): number[] {
  const out = [value & 127];
  while ((value >>= 7) > 0) out.unshift((value & 127) | 128);
  return out;
}

const PPQ = 96;
const ROW_TICKS = 24;

// notes only, one track per voice. a row is a sixteenth note, and the tempo track follows speed and tempo changes
export function midiFile(song: TrackerSong): Uint8Array {
  const line = timeline(song);
  const track = (events: Array<{ tick: number; bytes: number[] }>): number[] => {
    events.sort((a, b) => a.tick - b.tick);
    const body: number[] = [];
    let last = 0;
    for (const event of events) {
      body.push(...variable(event.tick - last), ...event.bytes);
      last = event.tick;
    }
    body.push(0, 0xff, 0x2f, 0);
    return [0x4d, 0x54, 0x72, 0x6b, (body.length >>> 24) & 255, (body.length >>> 16) & 255, (body.length >>> 8) & 255, body.length & 255, ...body];
  };
  const tempoEvents: Array<{ tick: number; bytes: number[] }> = [];
  let lastTempo = "";
  line.steps.forEach((step, index) => {
    const micros = Math.round(rowSeconds(step.speed, step.tempo) * ROW_TICKS * (1e6 / ROW_TICKS) * 4);
    if (String(micros) === lastTempo) return;
    lastTempo = String(micros);
    tempoEvents.push({ tick: index * ROW_TICKS, bytes: [0xff, 0x51, 3, (micros >> 16) & 255, (micros >> 8) & 255, micros & 255] });
  });
  const voiceCount = Math.max(1, ...song.patterns.flatMap((pattern) => pattern.notes.map((note) => note.c + 1)));
  const tracks = [track(tempoEvents)];
  for (let voice = 0; voice < voiceCount; voice++) {
    const channel = voice < 9 ? voice : Math.min(15, voice + 1);
    const events: Array<{ tick: number; bytes: number[] }> = [];
    let sounding = -1;
    let program = -1;
    line.steps.forEach((step, index) => {
      const note = song.patterns[step.pattern].notes.find((item) => item.r === step.row && item.c === voice);
      if (!note) return;
      const tick = index * ROW_TICKS;
      if (sounding >= 0) events.push({ tick, bytes: [0x80 | channel, sounding, 0] });
      sounding = -1;
      if (note.n === NOTE_CUT) return;
      if (note.s !== program) {
        program = note.s;
        events.push({ tick, bytes: [0xc0 | channel, program % 128] });
      }
      sounding = Math.min(127, note.n);
      events.push({ tick, bytes: [0x90 | channel, sounding, note.v >= 0 ? Math.max(1, Math.round((note.v / 64) * 127)) : 100] });
    });
    if (sounding >= 0) events.push({ tick: line.steps.length * ROW_TICKS, bytes: [0x80 | channel, sounding, 0] });
    if (events.length) tracks.push(track(events));
  }
  const head = [0x4d, 0x54, 0x68, 0x64, 0, 0, 0, 6, 0, 1, 0, tracks.length, PPQ >> 8, PPQ & 255];
  return Uint8Array.from([...head, ...tracks.flat()]);
}

// --- zip, stored without compression so a few small files need no library

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 255] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export function zipFiles(files: Array<{ name: string; data: Uint8Array }>): Uint8Array {
  const parts: number[][] = [];
  const central: number[][] = [];
  let offset = 0;
  const u16 = (value: number): number[] => [value & 255, (value >> 8) & 255];
  const u32 = (value: number): number[] => [value & 255, (value >>> 8) & 255, (value >>> 16) & 255, (value >>> 24) & 255];
  const chunks: Uint8Array[] = [];
  for (const file of files) {
    const name = Array.from(new TextEncoder().encode(file.name));
    const crc = crc32(file.data);
    const local = [...u32(0x04034b50), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0x21), ...u32(crc), ...u32(file.data.length), ...u32(file.data.length), ...u16(name.length), ...u16(0), ...name];
    parts.push(local);
    chunks.push(file.data);
    central.push([...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0x21), ...u32(crc), ...u32(file.data.length), ...u32(file.data.length), ...u16(name.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0), ...u32(0), ...u32(offset), ...name]);
    offset += local.length + file.data.length;
  }
  const centralSize = central.reduce((sum, entry) => sum + entry.length, 0);
  const end = [...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(files.length), ...u16(files.length), ...u32(centralSize), ...u32(offset), ...u16(0)];
  const size = offset + centralSize + end.length;
  const out = new Uint8Array(size);
  let at = 0;
  files.forEach((file, index) => {
    out.set(parts[index], at);
    at += parts[index].length;
    out.set(chunks[index], at);
    at += chunks[index].length;
  });
  for (const entry of central) {
    out.set(entry, at);
    at += entry.length;
  }
  out.set(end, at);
  return out;
}
